/**
 * PassVectorRedrawOverlay · inline re-draw editor for the 🟡 Edit
 * action in the triage panel.
 *
 * Minimal calibration-frame viewer with two pin-draggable handles for
 * the start + end pixels. Posts the new pair via the `correct` endpoint
 * (handled by the parent component which owns the network call).
 *
 * Pre-fills with the proposal's `*_image_xy` so the admin sees exactly
 * where the heuristic thought the throw + catch happened, and only has
 * to nudge whichever pin is off.
 *
 * Frame source:
 *   The release-moment frame is fetched via `/sequences/{id}/frame?t=`,
 *   with `t = clip.start_s + vector.timestamp_seconds`. Falls back to
 *   the static `cal.at_time_s` when the vector has no timestamp_seconds
 *   (manual draws, legacy data).
 *
 * Extracted from PassVectorTriage.jsx in the Phase A · Step 5 refactor.
 */
import React, { useEffect, useRef, useState } from "react";
import {
  CheckCircle2, Loader2, Pencil, RotateCcw, X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { frameUrl } from "@/lib/api";

export function PassVectorRedrawOverlay({
  clip, parent,
  initialStart, initialEnd,
  releaseTimestampSeconds,
  freezeOnFirstPin = false,
  onNudgeFrame,
  mode = "edit",        // "edit" | "create"
  onCancel, onSubmit,
}) {
  const cal = parent?.calibration || {};
  const imgRef = useRef(null);
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [start, setStart] = useState(initialStart || null);
  const [end, setEnd] = useState(initialEnd || null);
  const [active, setActive] = useState("start"); // "start" | "end"
  const [submitting, setSubmitting] = useState(false);
  const isCreate = mode === "create";

  // 🆕 2026-02-28 v4 · Two-Timestamp Anchoring. The R and C pins are
  // each placed at a different video moment — coaches scrub the player
  // from throw to catch with the backdrop tracking LIVE the whole
  // time. We snapshot the clip-local timestamp at each pin-placement
  // event and forward both to the persist call so the 3-D parabola
  // fitter can use the true (∆t, ∆x, ∆y) physics tuple. `null` means
  // the pin was placed without a live `releaseTimestampSeconds` prop
  // available (legacy edit-mode where the canvas isn't time-driven —
  // submit falls back to the existing `timestamp_seconds`).
  const [releaseTAtPin, setReleaseTAtPin] = useState(null);
  const [catchTAtPin, setCatchTAtPin] = useState(null);

  // Resolve the actual frame timestamp to render. Always live — the
  // user explicitly requested NO freeze so the catch position can be
  // pinned at a frame separate from the release. Pin coordinates are
  // in image-pixel space anchored to the calibration plane via the
  // homography matrix, so the visual R marker stays meaningful even
  // as the backdrop advances (the field hasn't moved between frames).
  let frameT = cal.at_time_s ?? 0;
  if (typeof releaseTimestampSeconds === "number" && typeof clip?.start_s === "number") {
    frameT = Math.max(0, clip.start_s + releaseTimestampSeconds);
  }
  const frameSrc = frameUrl(parent.id, frameT);

  // 🆕 2026-02-28 v3 · Frame-Perfect Stepping via ←/→ arrow keys.
  // While this overlay is mounted, intercept ArrowLeft/ArrowRight at
  // the window level and nudge the main video player by exactly one
  // 30 fps frame (~33.33 ms). Coaches gain frame-perfect release
  // capture without leaving the canvas to fiddle with the slider —
  // critical for 3 D parabola fitting + ML training data accuracy.
  //
  // Guardrails:
  //  • Skip when focus is on a text input / textarea / contenteditable
  //    so typing in the metadata fields keeps native caret nav.
  //  • `e.preventDefault()` blocks the default horizontal-scroll on
  //    pages where the body overflows; without it, holding `→` would
  //    scroll the wizard panel sideways while seeking.
  //  • Clamp at video.duration (browser actually clamps `currentTime`
  //    silently, but we explicitly forward the clamped value to
  //    `setTime()` in `nudgeFrame` so the React `time` state stays
  //    coherent with the player).
  //  • 30 fps assumption matches the existing harvest manifest target.
  //    A future enhancement could pull the real fps from
  //    `clip.tracking_json` or the <video> element's metadata.
  const FRAME_STEP_S = 1 / 30;
  useEffect(() => {
    if (typeof onNudgeFrame !== "function") return;
    const onKey = (e) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const ae = document.activeElement;
      const tag = ae?.tagName;
      const isText =
        tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT"
        || ae?.isContentEditable;
      if (isText) return;
      e.preventDefault();
      onNudgeFrame(e.key === "ArrowRight" ? FRAME_STEP_S : -FRAME_STEP_S);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onNudgeFrame]);

  const onImgLoad = () => {
    const im = imgRef.current;
    if (!im) return;
    setNatural({ w: im.naturalWidth, h: im.naturalHeight });
  };

  const clickToPixel = (e) => {
    const im = imgRef.current;
    if (!im || !natural.w) return null;
    const r = im.getBoundingClientRect();
    return {
      x: ((e.clientX - r.left) / r.width) * natural.w,
      y: ((e.clientY - r.top) / r.height) * natural.h,
    };
  };

  const handleClick = (e) => {
    const px = clickToPixel(e);
    if (!px) return;
    if (active === "start") {
      setStart(px);
      // Snapshot the LIVE clip-local timestamp at the moment R was
      // pinned — used as `release_t` on submit. The backdrop keeps
      // tracking live so the coach can scrub forward to the catch.
      setReleaseTAtPin(
        typeof releaseTimestampSeconds === "number" ? releaseTimestampSeconds : null
      );
      setActive("end");
    } else {
      setEnd(px);
      // Symmetric snapshot for C → `catch_t`. The persist payload
      // will carry both numbers so the parabola fitter can compute
      // true ∆t between throw and catch.
      setCatchTAtPin(
        typeof releaseTimestampSeconds === "number" ? releaseTimestampSeconds : null
      );
      setActive("start");
    }
  };

  const reset = () => {
    // "Restore proposal" in edit mode → snap back to the heuristic's
    // initial pins. "Clear pins" in create mode → wipe both pins to
    // null so the operator can start over. Also drop both timestamp
    // snapshots so the next pin-click re-captures live.
    setStart(isCreate ? null : (initialStart || null));
    setEnd(isCreate ? null : (initialEnd || null));
    setActive("start");
    setReleaseTAtPin(null);
    setCatchTAtPin(null);
  };

  const submit = async () => {
    if (!start || !end) {
      toast.error("Set both pins before saving");
      return;
    }
    setSubmitting(true);
    try {
      // Forward both pin-time snapshots so the create endpoint can
      // persist them. Edit-mode callers (PassVectorRow → onCorrected)
      // simply ignore the extra args — the existing API contract
      // `correctClipPassVector(clipId, vectorId, start, end)` is a
      // 2-arg JavaScript function, additional args are silently
      // dropped per ES spec.
      await onSubmit(start, end, releaseTAtPin, catchTAtPin);
    } finally {
      setSubmitting(false);
    }
  };

  const pinPct = (p) => p
    ? { left: `${(p.x / natural.w) * 100}%`, top: `${(p.y / natural.h) * 100}%` }
    : null;

  return (
    <div data-testid={`pass-vector-redraw-${clip.id}`} className="border border-amber-300/40 bg-amber-300/5 rounded-sm overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-amber-300/30">
        <div className="font-mono text-[11px] uppercase tracking-widest text-amber-200 flex items-center gap-2">
          <Pencil className="w-3.5 h-3.5" />
          {isCreate ? "New pass · click to set the" : "Re-draw active · click to set the"}
          <span className="font-bold mx-1">{active === "start" ? "RELEASE" : "CATCH"}</span>
          pin {active === "start" ? "(R)" : "(C)"}
          {typeof onNudgeFrame === "function" && (
            <span
              data-testid={`pv-redraw-frame-step-hint-${clip.id}`}
              className="ml-2 px-1.5 py-0.5 border border-amber-300/40 rounded-sm text-[9px] text-amber-200/80 normal-case tracking-normal"
              title="Step the video timeline by exactly 1 frame (~33ms @ 30fps)"
            >
              ← / → frame-step
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!isCreate && (
            <Button size="sm" variant="ghost" onClick={reset} disabled={submitting}
              className="h-6 px-2 font-mono text-[10px] uppercase tracking-widest hover:bg-accent hover:text-primary">
              <RotateCcw className="w-3 h-3 mr-1" /> Restore proposal
            </Button>
          )}
          {isCreate && (start || end) && (
            <Button size="sm" variant="ghost" onClick={reset} disabled={submitting}
              data-testid={`pass-vector-create-reset-${clip.id}`}
              className="h-6 px-2 font-mono text-[10px] uppercase tracking-widest hover:bg-accent hover:text-primary">
              <RotateCcw className="w-3 h-3 mr-1" /> Clear pins
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={submitting}
            data-testid={`pass-vector-redraw-cancel-${clip.id}`}
            className="h-6 px-2 font-mono text-[10px] uppercase tracking-widest">
            <X className="w-3 h-3 mr-1" /> Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={submitting || !start || !end}
            data-testid={isCreate ? `pass-vector-create-save-${clip.id}` : `pass-vector-redraw-save-${clip.id}`}
            className="h-7 px-3 bg-emerald-400/15 text-emerald-300 hover:bg-emerald-400 hover:text-black border border-emerald-400/40 font-display font-bold uppercase tracking-widest text-[10px]">
            {submitting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
            {isCreate ? "Save New Pass" : "Save Correction"}
          </Button>
        </div>
      </div>

      <div className="relative bg-black select-none" onClick={handleClick}
           style={{ cursor: submitting ? "wait" : "crosshair" }}>
        <img
          ref={imgRef} src={frameSrc} alt="calibration frame"
          onLoad={onImgLoad} draggable={false}
          className="block w-full h-auto"
        />
        {start && end && natural.w > 0 && (
          <svg className="absolute inset-0 w-full h-full pointer-events-none" preserveAspectRatio="none" viewBox={`0 0 ${natural.w} ${natural.h}`}>
            <defs>
              <marker id={`pv-redraw-arrow-${clip.id}`} viewBox="0 0 12 12" refX="10" refY="6"
                      markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                <path d="M 0 0 L 12 6 L 0 12 L 3 6 Z" fill="#FCD34D" />
              </marker>
            </defs>
            <line x1={start.x} y1={start.y} x2={end.x} y2={end.y}
                  stroke="rgba(252,211,77,0.25)" strokeWidth={natural.w / 80} strokeLinecap="round" />
            <line x1={start.x} y1={start.y} x2={end.x} y2={end.y}
                  stroke="#FCD34D" strokeWidth={natural.w / 200} strokeLinecap="round"
                  markerEnd={`url(#pv-redraw-arrow-${clip.id})`} />
          </svg>
        )}
        {start && (
          <div data-testid="pv-redraw-pin-r"
            className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-full bg-amber-300 text-black font-display font-black text-[13px] border-2 border-background shadow-lg shadow-amber-300/40 pointer-events-none"
            style={pinPct(start)}>R</div>
        )}
        {end && (
          <div data-testid="pv-redraw-pin-c"
            className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-full bg-amber-200 text-black font-display font-black text-[13px] border-2 border-background shadow-lg shadow-amber-300/40 pointer-events-none"
            style={pinPct(end)}>C</div>
        )}
      </div>

      <div className="px-3 py-1.5 border-t border-amber-300/30 font-mono text-[9px] uppercase tracking-widest text-muted-foreground flex items-center justify-between gap-3">
        <span>click anywhere on the frame to nudge the active pin · auto-toggles between R / C</span>
        {(releaseTAtPin != null || catchTAtPin != null) && (
          <span data-testid={`pv-redraw-pin-times-${clip.id}`} className="text-amber-200/80">
            {releaseTAtPin != null && <>R&nbsp;@&nbsp;<b>{releaseTAtPin.toFixed(3)}s</b></>}
            {releaseTAtPin != null && catchTAtPin != null && <>&nbsp;·&nbsp;∆t&nbsp;<b>{((catchTAtPin - releaseTAtPin)).toFixed(3)}s</b>&nbsp;·&nbsp;</>}
            {catchTAtPin != null && <>C&nbsp;@&nbsp;<b>{catchTAtPin.toFixed(3)}s</b></>}
          </span>
        )}
      </div>
    </div>
  );
}
